<template>
  <div>
    <div v-if="isInfluencerCell" class="if-cell">
      <el-image :src="profileImage" class="if-avatar" fit="cover" @error="onImgError" @click.native="openInfluencer">
        <div slot="error" class="if-avatar--error">
          <font-awesome-icon :icon="['far', 'user']" />
        </div>
      </el-image>

      <div class="if-meta">
        <div class="if-top">
          <span class="if-account" @click="openInfluencer">@{{ accountName }}</span>
          <span v-if="badge" class="if-badge">
            <font-awesome-icon :icon="['fas', 'badge-check']" />
          </span>
        </div>
        <div v-if="displayName" class="if-name" @click="openInfluencer">{{ displayName }}</div>
      </div>
    </div>

    <el-tooltip v-else-if="isEmptyValue" placement="top" :content="tooltipContent">
      <span class="na-hourglass">
        <font-awesome-icon :icon="['far', 'hourglass-half']" style="color: #ccc"/>
      </span>
    </el-tooltip>

    <span v-else-if="showEstimatedInsufficient">データ不足</span>

    <span v-else>
      {{ formattedValue }}
    </span>
  </div>
</template>

<script>
export default {
  name: "reportInfluencerValueInstagram",
  props: {
    column: { type: String, required: true },
    report_id: { type: [String, Number], required: true },
    ig_id: { type: [String, Number], default: null },
    index: { type: Number, required: true },
    value: { required: false },
    type: { type: String, default: "" },
    is_estimated_engagement_insufficient: { type: Boolean, default: false },
  },
  data() {
    return {
      imgFailed: false,
      s3BaseUrl: process.env.VUE_APP_S3_BASE_URL,
    };
  },
  computed: {
    isInfluencerCell() {
      return this.column === "influencer" || this.type === "media_and_if";
    },
    showEstimatedInsufficient() {
      if (this.isInfluencerCell) return false;
      if (!this.is_estimated_engagement_insufficient) return false;
      return (
        this.column === "average_like" ||
        this.column === "like_count" ||
        this.column === "average_engagement_rate"
      );
    },
    isEmptyValue() {
      if (this.isInfluencerCell) return false;
      if (this.showEstimatedInsufficient) return false;
      // オブジェクトが入ってきた場合や、空文字・nullの場合は空と判定
      if (this.value && typeof this.value === "object") return true;
      return this.value === null || this.value === undefined || this.value === "";
    },
    formattedValue() {
      if (this.isEmptyValue) return "";

      let val = this.value;

      switch (this.type) {
        case 'number':
        case 'cost_setting':
        case 'refer':
          return this.addComma(val);

        case 'percent':
        case 'refer_percent':
          return this.formatPercent(val) + "%";

        case 'cost':
          return "¥" + this.addComma(val);

        default:
          return val;
      }
    },
    // ツールチップ文言の出し分け
    tooltipContent() {
      switch (this.type) {
        case 'number':
        case 'cost_setting':
          return '投稿一覧で編集可能です';
        case 'percent':
          return '投稿一覧で指標を編集することで自動算出されます';
        case 'cost':
          return '投稿一覧でコストを設定することで自動算出されます';
        case 'refer':
        case 'refer_percent':
          return '該当のデータが存在するインフルエンサーのみ表示されます';
        default:
          return 'データがありません';
      }
    },
    // 以下、インフルエンサー表示用データ抽出
    row() {
      return (this.value && typeof this.value === "object") ? this.value : {};
    },
    influencer() {
      return this.row?.influencer || this.row || {};
    },
    accountName() {
      return this.influencer.account_name || this.row.account_name || "";
    },
    displayName() {
      return this.influencer.name || this.row.name || "";
    },
    badge() {
      const b = this.influencer.badge ?? this.row.badge;
      return Number(b) === 1;
    },
    profileImage() {
      if (this.imgFailed) {
        return this.s3BaseUrl ? `${this.s3BaseUrl}/userdata/img_error.png` : "";
      }
      return this.influencer.profile_picture_url || this.row.profile_picture_url || "";
    },
  },
  methods: {
    addComma(val) {
      if (val === "" || val === null || val === undefined) return "";
      if (isNaN(val)) return val;
      const n = Number(val);
      return Number.isInteger(n)
          ? n.toLocaleString()
          : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
    formatPercent(val) {
      if (val === "" || val === null || val === undefined || isNaN(val)) return val;
      return Math.round(Number(val) * 100) / 100;
    },
    onImgError() {
      this.imgFailed = true;
    },
    openInfluencer() {
      if (!this.accountName) return;
      this.$emit("openInfluencer", this.accountName);
    },
  },
};
</script>

<style scoped>
.if-cell { display: flex; align-items: center; gap: 10px; }
.na-hourglass { display: inline-flex; align-items: center; justify-content: center; }
.if-avatar { width: 34px; height: 34px; border-radius: 50%; cursor: pointer; }
.if-avatar--error { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #f2f2f2; color: #999; }
.if-meta { min-width: 0; }
.if-top { display: flex; align-items: center; gap: 6px; line-height: 1.1; }
.if-account { font-size: 12px; color: #333; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 220px; cursor: pointer; }
.if-name { font-size: 11px; color: #777; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 240px; cursor: pointer; }
.if-badge { font-size: 12px; color: #1da1f2; }
</style>